// Phase 44.21 — Card "Condividi tracking" da Customer/Driver Profile.
//
// Richiesto dall'utente: il modal share-tracking era accessibile solo dalla
// pagina di dettaglio ordine. Da profilo NON c'era un accesso rapido.
// Ora: card in profilo che fetcha gli ordini attivi e:
//   - 0 attivi   → empty-state pulito ("Nessuna consegna in corso")
//   - 1 attivo   → bottone "Condividi tracking" che apre il modal sull'ordine
//   - N attivi   → lista compatta di pulsanti, uno per ordine
//
// Cliente: full share (può generare/revocare link)
// Driver:  read-only (vede solo link già attivi del cliente, può copiare)

import { useEffect, useState } from "react";
import { Share2, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import ShareTrackingModal from "@/components/ShareTrackingModal";

// Stati "in corso" = non finali (delivered, cancelled, refunded)
const ACTIVE_STATUSES = new Set([
  "pending", "matching", "paid", "accepted",
  "picking_up", "picked_up", "delivering",
]);

export default function ShareTrackingProfileCard({ readOnly = false, testid = "share-tracking-profile-card" }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareOrderId, setShareOrderId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/orders/mine");
        if (!active) return;
        const list = Array.isArray(data) ? data.filter((o) => ACTIVE_STATUSES.has(o.status)) : [];
        // Ordinamento: i più recenti per primi
        list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setOrders(list);
      } catch {
        if (active) setOrders([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className="card-soft p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue shrink-0">
          <Share2 className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="overline">Condividi tracking</p>
          <p className="font-display text-lg font-bold tracking-tight mt-0.5">Invia il link al destinatario</p>
          <p className="text-xs text-dapnow-muted mt-1 leading-relaxed">
            {readOnly
              ? "Visualizza e copia il link tracking attivo per le consegne in corso."
              : "Genera un link pubblico per le tue consegne in corso. Il destinatario potrà consultare lo stato e, quando disponibili, la posizione del driver e la stima di arrivo, senza accedere all'app."}
          </p>
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-dapnow-muted" data-testid={`${testid}-loading`}>
            <Loader2 className="h-4 w-4 animate-spin" /> Caricamento ordini…
          </div>
        ) : orders.length === 0 ? (
          <div className="rounded-xl bg-dapnow-bg border border-dapnow-line px-4 py-3 text-sm text-dapnow-muted" data-testid={`${testid}-empty`}>
            Nessuna consegna in corso. Quando ne avrai una, potrai condividere il tracking da qui.
          </div>
        ) : (
          <ul className="space-y-2" data-testid={`${testid}-list`}>
            {orders.slice(0, 5).map((o) => {
              const shortId = (o.id || "").slice(0, 8).toUpperCase();
              return (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => setShareOrderId(o.id)}
                    data-testid={`${testid}-open-${o.id}`}
                    className="w-full flex items-center justify-between gap-3 rounded-xl border border-dapnow-line px-4 py-3 text-left hover:border-dapnow-blue hover:bg-dapnow-blue-soft transition group"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold tracking-tight text-sm truncate">Ordine #{shortId}</p>
                      <p className="text-xs text-dapnow-muted truncate">
                        {o.delivery_address || o.dropoff_address || "Consegna in corso"}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-dapnow-muted group-hover:text-dapnow-blue group-hover:translate-x-0.5 transition shrink-0" />
                  </button>
                </li>
              );
            })}
            {orders.length > 5 && (
              <li className="text-xs text-dapnow-muted px-1">
                + altri {orders.length - 5} ordini attivi. Apri dalla pagina ordine.
              </li>
            )}
          </ul>
        )}
      </div>

      {shareOrderId && (
        <ShareTrackingModal
          orderId={shareOrderId}
          open={!!shareOrderId}
          onClose={() => setShareOrderId(null)}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}
